import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import { UploadCloud } from 'lucide-react';
import { siteLanguagesService } from 'components/api/siteLanguages';
import { SiteLanguage } from 'types/siteLanguage';
import { showToast } from 'store/toastSlice';
import { useAppDispatch } from 'store/hooks';
import styles from './LangTab.module.scss';

interface UploadLogoModalProps {
  open: boolean;
  language: SiteLanguage | null;
  onClose: () => void;
  onSuccess: () => void;
}

const UploadLogoModal: React.FC<UploadLogoModalProps> = ({
  open,
  language,
  onClose,
  onSuccess,
}) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open && language) {
      const currentImg = language.logoUrl || null;
      setPreview(currentImg);
      setSelectedFile(null);
    } else {
      setSelectedFile(null);
      setPreview(null);
    }
  }, [open, language]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!language || !selectedFile) {
      dispatch(showToast({ text: 'Please select an image file first', type: 'error' }));
      return;
    }

    try {
      setUploading(true);
      await siteLanguagesService.uploadSiteLanguageLogo(language.id, selectedFile);
      dispatch(showToast({ text: 'Language logo updated successfully', type: 'success' }));
      onSuccess();
      onClose();
    } catch (error: unknown) {
      console.error('Failed to upload language logo:', error);
      let msg = 'Failed to upload logo';
      if (axios.isAxiosError(error)) {
        msg = error.response?.data?.message || error.message || msg;
      } else if (error instanceof Error) {
        msg = error.message;
      }
      dispatch(showToast({ text: msg, type: 'error' }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={uploading ? undefined : onClose}
      classes={{ paper: styles.dialogPaper }}
      maxWidth="xs"
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle className={styles.dialogTitle}>
        Upload Logo — {language?.name || language?.code?.toUpperCase()}
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className={styles.hiddenFileInput}
        />

        <div
          className={styles.logoUploadBox}
          onClick={() => fileInputRef.current?.click()}
        >
          {preview ? (
            <div className={styles.previewContainer}>
              <img src={preview} alt="Logo preview" className={styles.previewImg} />
              <span className={styles.uploadHint}>
                {selectedFile ? 'Selected file: ' + selectedFile.name : 'Current logo. Click to replace.'}
              </span>
            </div>
          ) : (
            <>
              <UploadCloud className={styles.uploadIcon} size={32} />
              <span className={styles.uploadText}>Click to select logo file</span>
              <span className={styles.uploadHint}>SVG, PNG, WEBP or JPG</span>
            </>
          )}
        </div>
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={onClose}
          disabled={uploading}
          sx={{ color: 'var(--text-secondary)', textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={uploading || !selectedFile}
          className={styles.addBtn}
        >
          {uploading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <CircularProgress size={16} sx={{ color: '#fff' }} />
              Uploading...
            </div>
          ) : (
            'Save Logo'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UploadLogoModal;
